import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Send, X, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestAccessModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const canSubmit = name.trim().length >= 2 && /\S+@\S+\.\S+/.test(email) && source.trim().length >= 2 && !busy;

  const reset = () => {
    setName(""); setEmail(""); setSource(""); setMessage(""); setBusy(false); setDone(false); setError(null);
  };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/access-request`, {
        name: name.trim(),
        email: email.trim(),
        source: source.trim(),
        message: message.trim() || null,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center px-4 animate-fade-in"
      onClick={close}
      data-testid="request-access-modal"
    >
      <div
        className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-700/60 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Request Access</h2>
            <p className="text-xs text-slate-400 mt-0.5">A superadmin will review and get in touch.</p>
          </div>
          <button
            onClick={close}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="request-access-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-10 text-center space-y-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-white font-semibold">Request sent</h3>
            <p className="text-sm text-slate-300 max-w-xs mx-auto">
              Thanks{name ? `, ${name.split(' ')[0]}` : ''}. We'll reach out at <b className="text-white">{email}</b> once you're approved.
            </p>
            <Button onClick={close} className="mt-4 bg-sky-600 hover:bg-sky-500" data-testid="request-access-done">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <Field label="Full name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="Jane Doe"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="request-access-name"
              />
            </Field>
            <Field label="Email address" required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
                placeholder="you@example.com"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="request-access-email"
              />
            </Field>
            <Field label="Where did you hear about us?" required>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                maxLength={200}
                placeholder="Referred by ..., Twitter, News article, etc."
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="request-access-source"
              />
            </Field>
            <Field label="Why do you need access?">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Optional — a couple of lines helps us route the request."
                className="w-full rounded-md bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 text-sm px-3 py-2 outline-none focus:ring-2 focus:ring-sky-500/40 resize-none"
                data-testid="request-access-message"
              />
            </Field>

            {error && (
              <p className="text-xs text-red-400" data-testid="request-access-error">{error}</p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                onClick={close}
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50"
                data-testid="request-access-submit"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-2" />Send Request</>}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-slate-400 mb-1.5 block">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
